import React, { useCallback } from "react";

import {
  ChevronSelectorVerticalDown,
  ChevronSelectorVerticalUp,
  HelpOutline,
} from "icons";
import { Tooltip } from "minimum-ui/core/Tooltip/Tooltip";
import { color } from "minimum-ui/tokens";
import { rem } from "polished";
import styled, { css } from "styled-components";

import { SortDirection, useTableContext } from "./Context";

export interface HeadCellProps {
  sortKey?: string;
  hint?: string;
  className?: string;
  $sticky?: boolean;
  $stickyOffset?: number;
  $right?: boolean;
}

const HeadCell: React.FC<HeadCellProps> = ({
  children,
  sortKey,
  hint,
  className,
  $sticky,
  $stickyOffset,
  $right,
}) => {
  const { onSort, sort } = useTableContext() ?? {};
  const handleSort = useCallback(() => {
    if (sortKey) {
      onSort?.(sortKey);
    }
  }, [onSort, sortKey]);

  const activeSort = sort?.field === sortKey;
  const currentSortDirection: SortDirection = activeSort
    ? sort?.direction ?? "asc"
    : "asc";

  let sortLabel = `Cancel sorting on '${sortKey}'`;
  if (!activeSort) {
    sortLabel = `Sort on '${sortKey}' ascending`;
  } else if (currentSortDirection === "asc") {
    sortLabel = `Sort on '${sortKey}' descending`;
  }

  return (
    <HeadCellWrapper
      className={className}
      $sticky={$sticky}
      $stickyOffset={$stickyOffset}
      $right={$right}
    >
      <Label>{children}</Label>
      {hint && (
        <Tooltip title={hint} placement="bottom">
          <Info />
        </Tooltip>
      )}
      {sortKey && (
        <Sorter
          onClick={handleSort}
          $dir={currentSortDirection}
          $active={activeSort}
          aria-label={sortLabel}
        >
          <Asc $active={activeSort && currentSortDirection === "asc"} />
          <Desc $active={activeSort && currentSortDirection === "desc"} />
        </Sorter>
      )}
    </HeadCellWrapper>
  );
};

export const HeadCellWrapper = styled.div<{
  $sticky?: boolean;
  $stickyOffset?: number;
  $right?: boolean;
}>`
  display: flex;
  align-items: center;
  padding: 13px 24px;
  font-size: ${rem(12)};
  line-height: ${rem(18)};
  font-weight: 500;
  color: ${color.neutral[500]};
  gap: 4px;
  text-align: left;
  background: ${color.neutral[100]};
  border-top: 1px solid ${color.border.default};
  border-bottom: 1px solid ${color.border.default};

  ${(props) =>
    props.$sticky &&
    css`
      background: ${color.neutral[100]};
      border-left: 1px solid ${color.border.default};
      position: sticky;
      right: 0;
      box-shadow: -5px 0 10px -1px ${color.neutral[200]};
    `}

  ${(props) =>
    props.$stickyOffset &&
    css`
      right: ${props.$stickyOffset}px;
    `}

  ${(props) =>
    props.$right &&
    css`
      justify-content: flex-end;
      text-align: right;
    `}
`;

const Info = styled(HelpOutline)`
  width: 16px;
  height: 16px;
  color: ${color.neutral[400]};
`;

const Asc = styled(ChevronSelectorVerticalUp)<{ $active: boolean }>`
  display: block;
  width: 17px;
  height: 16px;
  position: absolute;
  top: 0;
  left: 0;
  color: ${color.neutral[300]};
  ${(props) =>
    props.$active &&
    css`
      color: ${color.neutral[400]};
    `}
`;
const Desc = styled(Asc).attrs({ as: ChevronSelectorVerticalDown })``;
const Sorter = styled.button.attrs({ type: "button" })<{
  $dir: SortDirection;
  $active: boolean;
}>`
  background: none;
  border: 0;
  cursor: pointer;
  position: relative;
  width: 17px;
  height: 16px;
  padding: 0;
  position: relative;
`;
const Label = styled.span`
  display: block;
`;

export default HeadCell;
